import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import applicationService from "../../services/applicationService";
import jobOfferService from "../../services/jobOfferService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormSection from "../../components/ui/FormSection";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import "../../styles/FormCard.css";

const ApplicationNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    job_offer_id: "",
    cv_file: null,
    cover_letter: "",
    status: "PENDING",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadJobOffers();
  }, []);

  const loadJobOffers = async () => {
    try {
      setLoading(true);
      const response = await jobOfferService.getAll();
      const offersList = Array.isArray(response) ? response : (response?.data || []);
      setJobOffers(offersList);
    } catch (err) {
      console.error("Error loading job offers:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = "Le prénom est requis";
    if (!formData.last_name.trim()) newErrors.last_name = "Le nom est requis";
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    if (!formData.phone.trim()) newErrors.phone = "Le téléphone est requis";
    if (!formData.job_offer_id) newErrors.job_offer_id = "L'offre d'emploi est requise";
    if (!formData.cv_file) newErrors.cv_file = "Le CV est requis";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) return;

  try {
    setSubmitting(true);

    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      const value = formData[key];

      // Fichier CV
      if (key === "cv_file") {
        if (value) {
          formDataToSend.append("cv_file", value);
        }
      }
      // Autres champs
      else if (value !== null && value !== undefined && value !== "") {
        formDataToSend.append(key, value);
      }
    });

    // Debug : vérifier ce qui est réellement envoyé
    console.log("=== DONNÉES CANDIDATURE ===");

    for (const [key, value] of formDataToSend.entries()) {
      if (value instanceof File) {
        console.log(
          `${key}:`,
          value.name,
          value.type,
          value.size
        );
      } else {
        console.log(`${key}:`, value);
      }
    }

    // IMPORTANT : le service possède createWithCV(), pas create()
    const response = await applicationService.createWithCV(
      formDataToSend
    );

    console.log("Candidature créée :", response);

    toast.success("Candidature créée avec succès");

    navigate("/rh/applications");

  } catch (err) {
    console.error("Error creating application:", err);
    console.error("Response:", err.response?.data);

    toast.error(
      err.response?.data?.detail ||
      getErrorMessage(err)
    );

  } finally {
    setSubmitting(false);
  }
};



  const handleCancel = () => {
    navigate("/rh/applications");
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Candidatures", to: "/rh/applications" },
    { label: "Nouvelle candidature" }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard title="Nouvelle candidature" showBackButton onBack={handleCancel}>
        <form onSubmit={handleSubmit}>
          <FormSection title="Informations du candidat">
            <div className="form-group">
              <label>Prénom *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={errors.first_name ? "error" : ""}
              />
              {errors.first_name && <span className="error">{errors.first_name}</span>}
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={errors.last_name ? "error" : ""}
              />
              {errors.last_name && <span className="error">{errors.last_name}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? "error" : ""}
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Téléphone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? "error" : ""}
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>
          </FormSection>

          <FormSection title="Détails de la candidature">
            <div className="form-group">
              <label>Offre d'emploi *</label>
              <select
                name="job_offer_id"
                value={formData.job_offer_id}
                onChange={handleChange}
                className={errors.job_offer_id ? "error" : ""}
              >
                <option value="">Sélectionner une offre</option>
                {jobOffers.map(offer => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title}
                  </option>
                ))}
              </select>
              {errors.job_offer_id && <span className="error">{errors.job_offer_id}</span>}
            </div>
            <div className="form-group">
              <label>CV PDF *</label>
              <input
                type="file"
                name="cv_file"
                accept=".pdf"
                onChange={handleChange}
                className={errors.cv_file ? "error" : ""}
              />
              {errors.cv_file && <span className="error">{errors.cv_file}</span>}
            </div>
            <div className="form-group">
              <label>Lettre de motivation (facultative)</label>
              <textarea
                name="cover_letter"
                value={formData.cover_letter}
                onChange={handleChange}
                placeholder="Lettre de motivation..."
              />
            </div>
            <div className="form-group">
              <label>Statut initial</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="PENDING">En attente</option>
                <option value="PRESELECTED">Présélectionné</option>
                <option value="INTERVIEW">Entretien</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notes internes (facultatives)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notes internes..."
              />
            </div>
          </FormSection>

          <FormActions
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveText="Créer la candidature"
            isLoading={submitting}
          />
        </form>
      </FormCard>
    </div>
  );
};

export default ApplicationNew;

